// React Dependencies
import { React, useEffect } from 'react';
// Common Dependencies
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
// Components
import { Container, Form, Button } from 'react-bootstrap';
// Validation
import { useFormik } from 'formik';
import validationSchema from './OtpVerificationValidation';
// Alert
import { toast } from 'react-toastify';
// API Call
import { useResendOtp, useOTPVerify } from '@hooks';
// Page Components
import { PublicLayout, LogoUrl } from '@adminlayout';
import { TNButton } from '@common/components';
// Font Awesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
// Loaders
import { showLoader, hideLoader } from '../../../store/features/loaderSlice';
import CommonSEOTitle from '../../../../helpers/CommonSEOTitle';
// React Icons
import { MdKey } from 'react-icons/md';
// npm AOS animation
import AOS from 'aos';
import 'aos/dist/aos.css';

const OtpVerificationPage = ({ t, alert }) => {
  // Navigation And Dispatch
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // npm AOS animation
  useEffect(() => {
    AOS.init({ duration: 3000 });
  }, []);
  // Resent OTP
  const { mutate: doResendOtp } = useResendOtp(
    (response) => {
      dispatch(hideLoader());
      toast.success(response.message);
    },
    (error) => {
      dispatch(hideLoader());
      toast.error(error.message);
    }
  );
  // Navigate To Login Page
  const handleLoginButton = () => {
    alert({
      type: 'warning',
      title: 'Confirmation',
      message: t('page.login_reset_alert_popup_message'),
      confirmText: t('page.alert_popup_yes_button'),
      cancelText: t('page.alert_popup_no_button'),
      onConfirm: () => {
        navigate(`/login/`);
      }
    });
  };
  // OTP Validate And Submit
  const {
    mutate: doOtpVerify,
    isLoading: isLoadingOtpVerify,
    error: addOVList,
    isError: addOVError
  } = useOTPVerify(
    (response) => {
      toast.success(response.message);
      localStorage.verify_otp_token = response.data.user_id;
      localStorage.removeItem('forgot_token');
      navigate('/reset-password');
    },
    (error) => {
      window.scrollTo(0, 0);
      toast.error(error.message);
    }
  );
  // Add Class To The Body And Set Local Storage
  useEffect(() => {
    document.body.classList.add('bg-box');
    if (
      localStorage.isAdmin === undefined ||
      localStorage.isAdmin === '' ||
      localStorage.forgot_token === undefined ||
      localStorage.forgot_token === ''
    ) {
      navigate('/login');
    }
  });
  // Resend OTP
  const handleResend = () => {
    const values = {};
    values.user_id = localStorage.forgot_token;
    dispatch(showLoader());
    doResendOtp(values);
  };
  // Validate And Verify OTP
  const formik = useFormik({
    initialValues: {
      otp: ''
    },
    validationSchema,
    onSubmit: (values) => {
      values.user_id = localStorage.forgot_token;
      doOtpVerify(values);
    }
  });
  return (
    <>
      <CommonSEOTitle title={t('page.otp_verification_header_text')} />
      <PublicLayout>
        <div className="auth-pages">
          <div className="background-box" data-aos="fade-in">
            <div>
              <div className="brand-logo">
                <LogoUrl />
              </div>
              <div className="auth-heading">
                <h1 className="page-heading-center">{t('page.otp_verification_header_text')}</h1>
                <div
                  className="auth-subheading"
                  dangerouslySetInnerHTML={{
                    __html: t('page.otp_verification_sub_header_text')
                  }}></div>
              </div>
              <div>
                <Form onSubmit={formik.handleSubmit}>
                  <Form.Group className="form-field-group">
                    <Form.Label className="field-label">
                      {t('page.otp_verification_otp_label')}
                    </Form.Label>
                    <Form.Control
                      className={
                        'form-field ' +
                        (formik.touched.otp && formik.errors.otp
                          ? 'form-field-error'
                          : formik.touched.otp && !formik.errors.otp
                            ? 'form-field-success'
                            : '')
                      }
                      type="text"
                      name="otp"
                      maxLength="4"
                      onKeyUp={(event) => {
                        if (!/[0-9]/.test(event.key)) {
                          event.preventDefault();
                        }
                      }}
                      placeholder={t('page.otp_verification_otp_placeholder')}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.otp}
                    />{' '}
                    <MdKey className="otp-icon" />
                    {formik.touched.otp && formik.errors.otp ? (
                      <div className="form-field-error-text">{t(formik.errors.otp)}</div>
                    ) : null}
                  </Form.Group>
                  <div className="resend-otp-text">
                    <span>{t('page.otp_verification_resend_link_header_text')}</span>
                    <span onClick={handleResend}>
                      {t('page.otp_verification_resend_link_text')}
                    </span>
                  </div>
                  <div className="primary-button">
                    <span className="cancel-button" onClick={handleLoginButton}>
                      {t('page.otp_login_link_text')}
                    </span>
                    <TNButton
                      loading={isLoadingOtpVerify}
                      type="submit"
                      isdirtyform={formik.dirty && formik.dirty !== undefined ? 1 : 0}>
                      {t('page.otp_verification_submit_button_text')}
                    </TNButton>
                  </div>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </PublicLayout>
    </>
  );
};
OtpVerificationPage.propTypes = {
  t: PropTypes.func,
  isdirtyform: PropTypes.any
};
export { OtpVerificationPage };
